import COUNTRY_OPTIONS, {
  CountryCode,
  CURRENCY_CODES,
  CurrencyCode,
} from "@/constants/CountryOptions";
import {
  FormControl,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";
import { NumericFormat } from "react-number-format";

const currencySymbols: {
  [key in CurrencyCode]?: string;
} = {
  AUD: "A$",
  NZD: "NZ$",
};

function getSymbol(currencyCode: CurrencyCode, countryCode: CountryCode) {
  try {
    let formattedValue = (0)
      .toLocaleString("en", {
        style: "currency",
        currency: currencyCode,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })
      .replace(/\d/g, "")
      .trim();
    if (currencyCode === "AUD" && countryCode === "AU") {
      formattedValue = formattedValue.replace(/^A[$]/gi, "$");
    }
    return formattedValue;
  } catch {
    console.error("CLIENT_KNOWN_ERROR: failed to get currency symbol");
  }
  return currencySymbols[currencyCode] || "$";
}

export default function ControlledPriceField({
  name,
  label,
  countryName,
  currencyName,
}: {
  name: string;
  label: string;
  countryName: string;
  currencyName: string;
}) {
  const { control, watch, subscribe, setValue } = useFormContext();

  subscribe({
    name: [countryName],
    callback: () => {
      const countryCode = watch(countryName);
      const currencyCode = watch(currencyName);
      const price = watch(name);

      if (!price) {
        const country = COUNTRY_OPTIONS.find(
          (option) => option.code === countryCode
        );
        if (country && country.currency !== currencyCode) {
          setValue(currencyName, country.currency);
        }
      }
    },
  });

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { ref, ...fieldRest }, fieldState }) => (
        <NumericFormat
          label={label}
          customInput={TextField}
          inputRef={ref}
          {...fieldRest}
          onChange={undefined}
          onValueChange={({ value }) => {
            fieldRest.onChange(value);
          }}
          autoComplete="new-password"
          thousandSeparator={true}
          decimalScale={0}
          error={!!fieldState.error}
          helperText={fieldState.error ? fieldState.error.message : undefined}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  {getSymbol(watch(currencyName), watch(countryName))}
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <Controller
                    name={currencyName}
                    control={control}
                    render={({ field }) => (
                      <FormControl variant="standard">
                        <Select {...field}>
                          {CURRENCY_CODES.map((currency) => (
                            <MenuItem key={currency} value={currency}>
                              {currency}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                </InputAdornment>
              ),
            },
          }}
        />
      )}
    />
  );
}
